/**
 * The filter bar inside one expanded worklist group.
 *
 * Three filters only — rooftop, stock type, age range (Ian, 2026-08-13: "the
 * filtering options are only rooftop, stock type, age range"). Deliberately
 * narrower than the sheet view's FilterBar, which also carries search, Tekion
 * status and flag: a group has already answered "which problem", so the
 * remaining questions are which store, what kind of car, and how long it has
 * been sitting.
 *
 * Options come from the group itself, so a dropdown never offers a store whose
 * cars are not in front of the operator. Rendered only when the group has
 * something to filter by; Dealer Duplicates never renders it at all.
 */
import React from "react"

import { THEME } from "../../../config/theme"
import MultiSelectDropdown from "../MultiSelectDropdown"

export interface GroupFilters {
  rooftops: string[]
  vehicleTypes: string[]
  ageMin: number | null
  ageMax: number | null
}

export const EMPTY_GROUP_FILTERS: GroupFilters = {
  rooftops: [],
  vehicleTypes: [],
  ageMin: null,
  ageMax: null,
}

export interface GroupFilterOptions {
  rooftops: string[]
  vehicle_types: string[]
  age_range: { min: number; max: number }
}

// Every read of `options` is optional-chained. A group must not go blank
// because one field of the options response is missing — the rows are the
// point of the group, and the filters are an aid to reading them.

export function countActiveFilters(filters: GroupFilters): number {
  return (
    (filters.rooftops.length > 0 ? 1 : 0) +
    (filters.vehicleTypes.length > 0 ? 1 : 0) +
    (filters.ageMin !== null || filters.ageMax !== null ? 1 : 0)
  )
}

interface Props {
  options: GroupFilterOptions | null
  filters: GroupFilters
  onChange: (next: GroupFilters) => void
  /** Rows the filter is showing, and the group's full count, for "12 of 516". */
  shown: number
  total: number
  /** False while the page-level store view is active — one store's rows need
   *  no rooftop filter, so the dropdown hides rather than offering a no-op. */
  showRooftopFilter?: boolean
}

const inputStyle: React.CSSProperties = {
  width: 62,
  padding: "5px 7px",
  fontSize: 12,
  fontFamily: THEME.fonts.primary,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.sm,
  background: THEME.colors.white,
  color: THEME.colors.neutral.light.textPrimary,
}

export default function GroupFilterBar({
  options, filters, onChange, shown, total, showRooftopFilter = true,
}: Props) {
  const active = countActiveFilters(filters)

  // An empty string clears the bound rather than reading as 0 — "over 0 days"
  // is every car, which is not what backspacing a field means.
  //
  // Truncated to a whole number, and Infinity rejected alongside NaN, because
  // age is served by an integer query parameter: typing "12.5" (which a number
  // input accepts) sent 12.5 and came back a 422, which surfaced as a dead
  // group rather than as anything the operator could connect to their keystroke.
  const setBound = (key: "ageMin" | "ageMax") => (raw: string) => {
    const trimmed = raw.trim()
    const parsed = trimmed === "" ? null : Number(trimmed)
    onChange({
      ...filters,
      [key]: parsed === null || !Number.isFinite(parsed) ? null : Math.trunc(parsed),
    })
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: THEME.spacing.sm,
        flexWrap: "wrap",
        padding: "8px 16px",
        borderBottom: `1px solid ${THEME.colors.neutral.light.border}`,
        background: THEME.problemDisplay.legendBackground,
      }}
    >
      {showRooftopFilter && (
        <MultiSelectDropdown
          label="Rooftop"
          options={options?.rooftops ?? []}
          selected={filters.rooftops}
          onChange={(values) => onChange({ ...filters, rooftops: values })}
          disabled={options === null}
        />
      )}
      <MultiSelectDropdown
        label="Stock type"
        options={options?.vehicle_types ?? []}
        selected={filters.vehicleTypes}
        onChange={(values) => onChange({ ...filters, vehicleTypes: values })}
        disabled={options === null}
      />
      <span
        style={{
          display: "flex", alignItems: "center", gap: 6,
          fontSize: 12.5, color: THEME.colors.neutral.light.textSecondary,
        }}
      >
        Age
        <input
          type="number"
          value={filters.ageMin ?? ""}
          onChange={(e) => setBound("ageMin")(e.target.value)}
          placeholder={options?.age_range ? String(options.age_range.min) : "min"}
          aria-label="Minimum age in days"
          style={inputStyle}
        />
        <span aria-hidden="true">–</span>
        <input
          type="number"
          value={filters.ageMax ?? ""}
          onChange={(e) => setBound("ageMax")(e.target.value)}
          placeholder={options?.age_range ? String(options.age_range.max) : "max"}
          aria-label="Maximum age in days"
          style={inputStyle}
        />
        days
      </span>

      {active > 0 && (
        <>
          {/* The group header keeps showing the group's true size, so without
              this the header and the rows below it appear to disagree. */}
          <span
            style={{
              fontSize: 12.5,
              fontVariantNumeric: "tabular-nums",
              color: THEME.colors.neutral.light.textPrimary,
            }}
          >
            Showing {shown.toLocaleString()} of {total.toLocaleString()}
          </span>
          <button
            type="button"
            onClick={() => onChange(EMPTY_GROUP_FILTERS)}
            style={{
              padding: "4px 10px",
              fontSize: 12,
              fontFamily: THEME.fonts.primary,
              borderRadius: THEME.borderRadius.sm,
              border: `1px solid ${THEME.colors.silver}`,
              background: THEME.colors.white,
              color: THEME.colors.neutral.light.textPrimary,
              cursor: "pointer",
            }}
          >
            Clear
          </button>
        </>
      )}
    </div>
  )
}
